import React from 'react';
import * as Sentry from '@sentry/react';
import {
  toast,
} from 'react-toastify';
import i18n from 'i18next';
import MobileDetect from 'mobile-detect';
import mixpanel from 'mixpanel-browser';

import {
  api,
} from '@gymcloud/core';

import config from 'web-config';
import router from 'web-router';

const env = process.env.TARGET_ENV;

export function throwError(err: any) {
  if (env !== 'development') {
    if (err.request) {
      Sentry.withScope((scope) => {
        scope.setExtra('url', err.request.responseURL);
        scope.setExtra('response', err.request.response);
        scope.setExtra('responseText', err.request.responseText);
        scope.setExtra('responseStatus', err.request.status);
        scope.setExtra('responseStatusText', err.request.statusText);
        /* It need to have a correct stacktrace */
        /* TODO: stack trace still is not ideal, handle it somehow */
        Sentry.captureException(new Error(err));
      });
    } else {
      Sentry.captureException(err);
    }
  }
}

export function analyticsIdentify(profile: any) {
  if (env !== 'development') {
    // Sentry.configureScope((scope) => {
    //   scope.setUser({
    //     id: profile.id,
    //     email: profile.email,
    //     namespace: profile.namespace,
    //   });
    // });
    mixpanel.identify(profile.id);
    const property = {
      isPro: profile.is_pro,
      isDummy: profile.is_dummy,
      isTrialing: profile.is_trialing,
      'User Name': `${profile.user_profile.first_name} ${profile.user_profile.last_name}`,
      'User Email': profile.email,
      'User Namespace': profile.namespace,
    };
    mixpanel.register(property);
    mixpanel.people.set(property);
  }
}

// TODO Refactoring, do better error handling
export function initialApi() {
  const onSuccessRequests = response => response.data;
  const onErrorRequests = error => {
    const status = error?.request?.status;
    const json = error?.response?.data || {};
    let body = json.error || json.errors;
    const pathname = error.response?.config?.url?.pathname;
    const localeText = typeof json.error === 'string' && i18n.t(`error.${json.error}`, { defaultValue: null });

    if (localeText) {
      body = localeText;
    } else if (json.error === 'invalid_grant') {
      body = (
        <>
          {'Try again or '}
          <a
            href="https://gymcloud.freshdesk.com/support/tickets/new"
            rel="noopener noreferrer"
            target="_blank"
          >
            contact support here.
          </a>
        </>
      );
    } else if (json.error_description) {
      body = json.error_description;
    } else if (body?.reset_password_token) {
      body = i18n.t('error.reset_password_token');
    } else if (Array.isArray(body)) {
      body = body.join('. ');
    } else if (typeof body === 'object') {
      body = Object.keys(body).map(
        k => `${k}: ${Array.isArray(body[k]) ? body[k].join('. ') : body[k]}`,
      ).join('\n');
    }

    if (pathname === '/subscriptions') {
      if (body.startsWith('No such coupon:')) {
        body = i18n.t('payment.coupon_invalid');
      } else if (body.startsWith('Coupon expired:')) {
        body = i18n.t('payment.coupon_expired');
      }
    }

    // || status === 403 ? #TODO handle codes
    if (status === 401 && window.location.pathname.indexOf('/login') === -1) {
      router.navigate('/login');
      api.setAccessToken(null);
      api.clearAdditionalParams();
      localStorage.removeItem(
        'userId',
      );
      localStorage.removeItem(
        'isPro',
      );
      localStorage.removeItem(
        'accessToken',
      );
    } else if (status === 402) {
      router.navigate('/payment_required');
    }

    if (body && (typeof body === 'string' || React.isValidElement(body))) {
      toast.error(body);
    }

    throwError(error);
    return Promise.reject(error);
  };
  api.setApiUrl(config.apiUrl);
  api.instance.interceptors.response.use(onSuccessRequests, onErrorRequests);
}

const patternToRegex = (pattern: string): RegExp => {
  // Remove leading/trailing slashes from pattern
  const cleanPattern = pattern.replace(/^\/|\/$/g, '');

  return new RegExp(
    '^/?' + // Make leading slash optional with /?
    cleanPattern
      .replace(/:[^\/]+/g, '[^\/]+')
      .replace(/\*/g, '.*')
    + '$'
  );
};

const findMatchedRoute = (routes: any[], pathname: string): any => {
  for (const route of routes) {
    const regex = patternToRegex(route.path);
    if (regex.test(pathname) && route.meta?.track) {
      return route;
    }

    if (route.children) {
      const matchedChild = findMatchedRoute(route.children, pathname);
      if (matchedChild) return matchedChild;
    }
  }

  return null;
}

const extractWordsBetweenSlashes = (pathname: string) => {
  // Remove leading and trailing slashes
  const cleanPath = pathname.replace(/^\/|\/$/g, '');

  // Split the path and take the first segment
  const firstSegment = cleanPath.split('/')[0];

  // Remove '_folder' if present
  const withoutFolder = firstSegment.replace(/_folder$/, '');

  if (pathname.startsWith('/users/me')) {
    return 'My Training Log';
  }

  // Replace underscores and hyphens with spaces, then capitalize each word
  return withoutFolder
    .replace(/[_-]/g, ' ')
    .replace(/\w+/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}

export const trackPageView = ({ location }: any) => {
  if (!location?.pathname || location.pathname === '/') return;

  const matchedRoute = findMatchedRoute(router.routes, location.pathname);
  if (!matchedRoute) return;

  const name = extractWordsBetweenSlashes(location.pathname);
  mixpanel.track(
    `Viewed ${name}`,
    {
      name,
      path: location.pathname,
    },
  );

}
